import { css } from "emotion";
import { Component } from "preact";
import { route } from "preact-router";
import queryString from "query-string";
import React from "react";
import { geolocated } from "react-geolocated";
import {
  connectSearchBox,
  connectStateResults
} from "react-instantsearch/connectors";
import {
  Configure,
  Hits,
  InstantSearch,
  Pagination,
  ScrollTo
} from "react-instantsearch/dom";
import { connect } from "react-redux";
import config from "../../config";
import {
  clearRefinements,
  getTags,
  setRefinements
} from "../../state/actions/searchActions";
import { lastOfType, max } from "../../utils/CssUtils";
import CMSComponent from "../CMSComponent";
import Accordion from "../Menus/Accordion";
import Dropdown from "../Menus/Dropdown";
import FullScreenMenu from "../Menus/FullScreen";
import Card from "./card/Card";
import PillHandler from "./pills/PillHandler";
import DateRefinementMenu from "./refinementMenus/dateRefinementMenu";
import LocationRefinement from "./refinementMenus/locationRefinement";
import OptionsRefinementMenu from "./refinementMenus/OptionsRefinementMenu";
import SearchServices from "./searchServices";

import { camelize } from "../../utils/Format";

const noResultsImage = require("../../assets/image_no_results.svg");
const magnifyingGlass = require("../../assets/ic_magnifying_glass.svg");

const possibleCategories = [
  "Arts",
  "Business",
  "Charity or cause",
  "Community",
  "Conference",
  "Family & Education",
  "Fashion",
  "Festival",
  "Film & Media",
  "Food & Drink",
  "Government",
  "Health",
  "Home & Lifestyle",
  "Music",
  "Professional",
  "School Activities",
  "Science & Tech",
  "Spirituality",
  "Sports & Fitness",
  "Travel & Outdoor",
  "Workshop",
  "Comedy",
  "Design"
].sort();

const possibleAccessabilityRefinements = [
  "Access",
  "Wheelchair Accessibility",
  "Audio Description",
  "Telephone Typewriter",
  "Volume Control Telephone",
  "Assistive Listening Systems",
  "Sign Language Interpretation",
  "Accessible Print",
  "Closed Captioning",
  "Opened Captioning",
  "Braille Symbol"
].sort();

const wrapper = css({
  maxWidth: 1040,
  margin: "0 auto",
  paddingTop: 24,
  [max(1040)]: {
    padding: "14px 14px"
  }
});

const locationDisplay = css({
  fontSize: 26,
  marginBottom: 16,
  [max(650)]: {
    fontSize: 20
  }
});

const searchBarWrapper = css({
  padding: "14px 20px",
  backgroundColor: "white",
  borderRadius: 4,
  boxShadow: "0 1px 8px 0 rgba(32, 7, 64, 0.13)",
  marginBottom: 32,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between"
});

const resultsWrapper = css({
  ul: {
    listStyleType: "none",
    marginLeft: 0,
    padding: 0,
    "&:after": {
      content: '" "',
      clear: "both",
      display: "block"
    }
  },
  li: {
    float: "left",
    padding: 0,
    margin: 0,
    marginRight: 16,
    "&:nth-child(4n)": {
      marginRight: 0
    },
    [max(1040)]: {
      marginRight: 16,
      "&:nth-child(4n)": {
        marginRight: 16
      },
      "&:nth-child(3n)": {
        marginRight: 0
      }
    },
    [max(803)]: {
      marginRight: 16,
      "&:nth-child(4n)": {
        marginRight: 16
      },
      "&:nth-child(3n)": {
        marginRight: 16
      },
      "&:nth-child(2n)": {
        marginRight: 0
      }
    }
  }
});

const searchBoxStyle = css({
  maxWidth: 300,
  height: 40,
  width: "100%",
  border: "1px solid #8d949e",
  borderRadius: 4,
  fontSize: 16,
  backgroundImage: `url(${magnifyingGlass})`,
  backgroundRepeat: "no-repeat",
  paddingLeft: 34,
  backgroundPosition: "10px 11px",
  marginRight: 12
});

const noResultsStyling = css({
  textAlign: "center"
});

const desktop = css({
  [max(850)]: {
    display: "none"
  }
});

const mobile = css({
  display: "none",
  [max(850)]: {
    display: "block"
  },
  input: {
    marginRight: 0,
    width: "100%",
    maxWidth: "none",
    marginBottom: 10
  }
});

const filtersButton = css({
  width: "100%",
  height: 36,
  background: "transparent",
  border: "solid 1px #5A626C",
  borderRadius: 4,
  color: "#5A626C",
  backgroundImage: "url('../../assets/ic_filter.svg')",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "calc(50% - 33px) 5px",
  paddingLeft: "calc(50% - 20px)",
  textAlign: "left",
  fontSize: 14,
  marginBottom: 24
});

const showResultsButton = css({
  width: "100%",
  border: "none",
  color: "white",
  backgroundColor: "#6621c0",
  fontSize: 14,
  borderRadius: 4,
  textAlign: "center",
  height: 36
});

const mobileLocationWrapper = css({
  borderBottom: "1px solid #dedede",
  padding: "8px 14px",
  input: {
    width: "calc(100vw - 28px)"
  }
});

const dropdownWrappers = css({
  ">div": {
    marginRight: 16,
    [lastOfType()]: {
      marginRight: 0
    }
  }
});

const clearButton = css({
  color: "#6621c0",
  textDecoration: "underline"
});

const CardTemplate = ({ hit }) => {
  return <Card {...hit} />;
};

class EventSearchBox extends Component {
  componentDidUpdate(prevProps) {
    const { refine, value } = this.props;
    if (prevProps.value !== value) {
      refine(value);
    }
  }

  render() {
    const { value, saveQuery } = this.props;
    return (
      <input
        type="search"
        value={value}
        onKeyUp={e => saveQuery(e.target.value)}
        placeholder="Search Events"
        alt="Search Bar"
        aria-label="Search Events"
        className={searchBoxStyle}
      />
    );
  }
}

const ConnectedSearchBox = connectSearchBox(EventSearchBox);

const Content = connectStateResults(({ searchResults }) => {
  if (!searchResults) {
    return null;
  }
  return searchResults.nbHits !== 0 ? (
    <div>
      <div className={resultsWrapper}>
        <ScrollTo>
          <Hits hitComponent={CardTemplate} />
        </ScrollTo>
      </div>
      <Pagination showFirst={false} padding={2} />
    </div>
  ) : (
    <div className={noResultsStyling}>
      <img src={noResultsImage} alt="No results" />
      <h3>No Results Found</h3>
      <p>Sorry, we didn't find any results you were looking for</p>
      <p>Please give it another try</p>
    </div>
  );
});

class SearchComponent extends Component {
  initialLocationDetect = () => {
    const { coords, searchComponent, setRefinements } = this.props;
    SearchServices.getAreaFromLatLng(coords.latitude, coords.longitude)
      .then(res => {
        const result = res.data.results[0];
        const components = result.address_components;
        const part = components.find(component =>
          component.types.includes("locality")
        );
        const name = part.long_name;
        if (searchComponent && !searchComponent.address) {
          setRefinements({
            displayAddress: name,
            address: name,
            latlng: `${coords.latitude}, ${coords.longitude}`
          });
        }
      })
      .catch(err => {
        console.log(err);
      });
  };

  updateQuery = query => {
    this.props.setRefinements({ query: query });
  };

  toggleMenu = menu => {
    this.setState({
      [menu]: !this.state[menu]
    });
  };

  tagsUpdate = option => {
    const { searchComponent, setRefinements } = this.props;
    const { tags } = searchComponent;
    if (tags.indexOf(option) === -1) {
      setRefinements({
        tags: tags.concat([option])
      });
    } else {
      setRefinements({
        tags: tags.filter(v => v !== option)
      });
    }
  };

  accessibilityUpdate = option => {
    const { searchComponent, setRefinements } = this.props;
    const { accessibility } = searchComponent;
    if (accessibility.indexOf(option) === -1) {
      setRefinements({ accessibility: accessibility.concat([option]) });
    } else {
      setRefinements({
        accessibility: accessibility.filter(v => v !== option)
      });
    }
  };

  categoriesUpdate = option => {
    const { searchComponent, setRefinements } = this.props;
    const { categories } = searchComponent;
    if (categories.indexOf(option) === -1) {
      setRefinements({ categories: categories.concat([option]) });
    } else {
      setRefinements({ categories: categories.filter(v => v !== option) });
    }
  };

  setDateRefinement = t => {
    const { setRefinements } = this.props;
    let dateRefinementObj = SearchServices.getDateRefinement(t, new Date());
    if (dateRefinementObj && camelize(t) !== "all") {
      setRefinements({
        date: {
          refinement: camelize(t),
          startDate: dateRefinementObj.startDate,
          endDate: dateRefinementObj.endDate,
          pillInformation: dateRefinementObj.dateRefinement
        }
      });
    } else {
      setRefinements({ date: null });
    }
  };

  setCustomDateRefinement = t => {
    const { setRefinements, searchComponent } = this.props;
    const { date } = searchComponent;
    const refinementDate = new Date(t.value);
    refinementDate.setHours(0);
    let customDateRefinementObj = {
      refinement: "custom",
      pillInformation: "Custom",
      startDate: null,
      endDate: null
    };
    if (date && date.refinement === "custom") {
      customDateRefinementObj = { ...date };
    }
    customDateRefinementObj[t.field] = refinementDate.getTime();
    customDateRefinementObj[
      `${t.field}Display`
    ] = `${refinementDate.getDate()}/${refinementDate.getMonth() +
      1}/${refinementDate.getFullYear()}`;
    setRefinements({ date: customDateRefinementObj });
  };

  updateURL = () => {
    const { locale, searchComponent } = this.props;
    const { userId } = this.state;
    const refinements = [];

    if (searchComponent.hideTags) {
      refinements.push(`hidetags=true`);
    }

    searchComponent.categories.forEach(v =>
      refinements.push(`category[]=${encodeURIComponent(v)}`)
    );
    if (searchComponent.tags.length) {
      refinements.push(
        `tags=${encodeURIComponent(JSON.stringify(searchComponent.tags))}`
      );
    }
    searchComponent.accessibility.forEach(v =>
      refinements.push(`accessibility[]=${encodeURIComponent(v)}`)
    );
    searchComponent.bounds.forEach(v =>
      refinements.push(`bounds[]=${encodeURIComponent(v)}`)
    );
    if (searchComponent.date) {
      refinements.push(
        `date=${encodeURIComponent(JSON.stringify(searchComponent.date))}`
      );
    }
    if (searchComponent.query) {
      refinements.push(`query=${encodeURIComponent(searchComponent.query)}`);
    }
    if (searchComponent.displayAddress) {
      refinements.push(
        `address=${encodeURIComponent(searchComponent.address)}`
      );
      refinements.push(
        `displayAddress=${encodeURIComponent(searchComponent.displayAddress)}`
      );
      refinements.push(`latlng=${encodeURIComponent(searchComponent.latlng)}`);
    }
    if (userId) {
      refinements.push(`userid=${userId}`);
    }
    if (history.pushState) {
      var newurl =
        window.location.protocol +
        "//" +
        window.location.host +
        window.location.pathname +
        "?" +
        refinements.join("&");
      window.history.pushState({ path: newurl }, "", newurl);
    }
  };

  getRefinementQuery = () => {
    const { searchComponent } = this.props;
    const { categories, accessibility, date, tags, userId } = searchComponent;
    let currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    let gmtTime = new Date(
      currentDate
        .toString()
        .substring(0, 29)
        .concat("0000")
    );
    let time = gmtTime.getTime();
    const refinementsStages = [];
    //Only future dates
    refinementsStages.push(`endDateSort >= ${time}`);

    //custom dates
    let dateQueries = [];
    if (date && date.startDate !== null) {
      dateQueries.push(`dates.startDate >= ${date.startDate}`);
    }
    if (date && date.endDate !== null) {
      dateQueries.push(`dates.endDate <= ${date.endDate}`);
    }
    if (dateQueries.length) {
      refinementsStages.push(`(${dateQueries.join(" AND ")})`);
    }

    // categories stage
    const categoriesRefinementsKeys = categories.map(item => {
      return `categories:${camelize(item)}`;
    });
    if (categoriesRefinementsKeys.length) {
      refinementsStages.push(`(${categoriesRefinementsKeys.join(" OR ")})`);
    }

    // accessibility stage
    const accessibilityRefinementsKeys = accessibility.map(item => {
      return `accessibilityFeatures:${camelize(item)}`;
    });

    if (accessibilityRefinementsKeys.length) {
      refinementsStages.push(`(${accessibilityRefinementsKeys.join(" OR ")})`);
    }

    // tags stage
    const tagsRefinementsKeys =
      tags && tags.length
        ? tags.map(item => {
            return `tags:${item._id}`;
          })
        : false;
    if (tagsRefinementsKeys && tagsRefinementsKeys.length) {
      refinementsStages.push(`(${tagsRefinementsKeys.join(" OR ")})`);
    }
    // user id
    if (userId) {
      refinementsStages.push(`userId:${userId}`);
    }

    const refinementsQuery = refinementsStages.join(" AND ");
    return refinementsQuery;
  };
  constructor(props) {
    super(props);
    this.state = {
      categories: false,
      accessibility: false,
      date: false,
      customDate: false,
      filers: false,
      userId: false
    };
    const { getTags, content, setRefinements } = props;
    const search = typeof window !== "undefined" ? window.location.search : "";
    const parsed = queryString.parse(search, { arrayFormat: "bracket" });
    const refinementsObj = {};
    if (parsed.category) {
      refinementsObj.categories = parsed.category;
    }

    if (parsed.hidetags === "true") {
      refinementsObj.hideTags = true;
    }

    if (parsed.accessibility) {
      refinementsObj.accessibility = parsed.accessibility;
    }
    if (parsed.bounds) {
      refinementsObj.bounds = parsed.bounds;
    }
    if (parsed.date) {
      refinementsObj.date = JSON.parse(parsed.date);
    }
    if (parsed.query) {
      refinementsObj.query = parsed.query;
    }
    if (parsed.tags) {
      refinementsObj.tags = JSON.parse(parsed.tags);
    }
    if (parsed.displayAddress) {
      (refinementsObj.displayAddress = parsed.displayAddress),
        (refinementsObj.address = parsed.address),
        (refinementsObj.latlng = parsed.latlng);
    }
    let userId = parsed.u ? parsed.u[0] : false;
    userId = parsed.userid ? parsed.userid : userId;
    if (userId) {
      this.state.userId = userId;
      getTags(userId, content.local);
      refinementsObj.userId = userId;
    }
    setRefinements(refinementsObj);
  }

  componentDidUpdate(prevProps) {
    const {
      isWidget,
      coords,
      matches,
      setRefinements,
      searchComponent = {}
    } = this.props;
    const { categories = [] } = searchComponent;
    if (
      matches &&
      matches["category[]"] &&
      categories.indexOf(matches["category[]"]) === -1
    ) {
      setRefinements({ categories: [matches["category[]"]] });
    }
    if (!prevProps.coords && coords && !isWidget) {
      this.initialLocationDetect();
    }
    this.updateURL();
  }
  render() {
    const { searchComponent, clearRefinements, tags } = this.props;
    const selectedAccessibility =
      searchComponent && searchComponent.accessibility
        ? searchComponent.accessibility
        : [];

    const selectedTags =
      searchComponent && searchComponent.tags ? searchComponent.tags : [];

    const selectedCategories =
      searchComponent && searchComponent.categories
        ? searchComponent.categories
        : [];
    const currentQuery =
      searchComponent && searchComponent.query ? searchComponent.query : "";
    const refinementsQuery = this.getRefinementQuery();
    const latlng =
      searchComponent && searchComponent.latlng ? searchComponent.latlng : null;
    const bounds =
      searchComponent && searchComponent.bounds ? searchComponent.bounds : null;
    const customStartDateDisplay =
      searchComponent &&
      searchComponent.date &&
      searchComponent.date.startDateDisplay
        ? searchComponent.date.startDateDisplay
        : "Start Date";
    const customEndDateDisplay =
      searchComponent &&
      searchComponent.date &&
      searchComponent.date.endDateDisplay
        ? searchComponent.date.endDateDisplay
        : "End Date";

    const desktopDisplay = (
      <div className={desktop}>
        <div className={searchBarWrapper}>
          <div style={{ display: "flex" }}>
            <ConnectedSearchBox
              value={currentQuery}
              saveQuery={this.updateQuery}
            />
            <LocationRefinement />
          </div>
          <div className={dropdownWrappers}>
            <Dropdown
              title="Categories"
              open={this.state.categories}
              openToggle={() => this.toggleMenu("categories")}
              hasDone={true}
            >
              <div style={{ width: 200 }}>
                <OptionsRefinementMenu
                  options={possibleCategories}
                  active={selectedCategories}
                  update={this.categoriesUpdate}
                />
              </div>
            </Dropdown>
            <Dropdown
              title="Dates"
              open={this.state.date}
              openToggle={() => this.toggleMenu("date")}
            >
              <div style={{ width: "230px" }}>
                <DateRefinementMenu
                  setDateRefinement={this.setDateRefinement}
                  customDayChange={this.setCustomDateRefinement}
                  startDate={customStartDateDisplay}
                  endDate={customEndDateDisplay}
                  customMenuDirection="right"
                  customDateMenuToggle={() => this.toggleMenu("customDate")}
                  customDateOpen={this.state.customDate}
                />
              </div>
            </Dropdown>
            <Dropdown
              title="Accessibility"
              open={this.state.accessibility}
              openToggle={() => this.toggleMenu("accessibility")}
              hasDone={true}
            >
              <div style={{ width: 200 }}>
                <OptionsRefinementMenu
                  options={possibleAccessabilityRefinements}
                  active={selectedAccessibility}
                  update={this.accessibilityUpdate}
                />
              </div>
            </Dropdown>
            {tags.data &&
            tags.data.length &&
            searchComponent.hideTags !== true ? (
              <Dropdown
                title="Tags"
                open={this.state.tags}
                openToggle={() => this.toggleMenu("tags")}
                hasDone={true}
              >
                <div style={{ minWidth: 200 }}>
                  <OptionsRefinementMenu
                    options={tags.data}
                    active={selectedTags}
                    update={this.tagsUpdate}
                  />
                </div>
              </Dropdown>
            ) : null}
          </div>
        </div>
      </div>
    );

    const mobileDisplay = (
      <div className={mobile}>
        <ConnectedSearchBox value={currentQuery} saveQuery={this.updateQuery} />
        <button
          className={filtersButton}
          onClick={() => this.toggleMenu("filters")}
          aria-label="Filters"
        >
          Filters
        </button>

        <FullScreenMenu
          title="Filters"
          open={this.state.filters}
          close={() => this.toggleMenu("filters")}
          extraFunctionTop={
            <a onClick={clearRefinements} className={clearButton}>
              Clear
            </a>
          }
          extraFunctionBottom={
            <button
              className={showResultsButton}
              onClick={() => this.toggleMenu("filters")}
              aria-label="Show Results"
            >
              Show Results
            </button>
          }
        >
          <div className={mobileLocationWrapper}>
            <LocationRefinement />
          </div>
          <Accordion
            title="Categories"
            open={this.state.categories}
            openToggle={() => this.toggleMenu("categories")}
          >
            <OptionsRefinementMenu
              options={possibleCategories}
              active={selectedCategories}
              update={this.categoriesUpdate}
            />
          </Accordion>
          <Accordion
            title="Dates"
            open={this.state.date}
            openToggle={() => this.toggleMenu("date")}
          >
            <DateRefinementMenu
              setDateRefinement={this.setDateRefinement}
              customDayChange={this.setCustomDateRefinement}
              startDate={customStartDateDisplay}
              endDate={customEndDateDisplay}
              customMenuDirection="right"
              customDateMenuToggle={() => this.toggleMenu("customDate")}
              customDateOpen={this.state.customDate}
            />
          </Accordion>

          <Accordion
            title="Accessibility"
            open={this.state.accessibility}
            openToggle={() => this.toggleMenu("accessibility")}
          >
            <OptionsRefinementMenu
              options={possibleAccessabilityRefinements}
              active={selectedAccessibility}
              update={this.accessibilityUpdate}
            />
          </Accordion>
          {tags.data && tags.data.length ? (
            <Accordion
              title="Tags"
              open={this.state.tags}
              openToggle={() => this.toggleMenu("tags")}
            >
              <OptionsRefinementMenu
                options={tags.data}
                active={selectedTags}
                update={this.tagsUpdate}
              />
            </Accordion>
          ) : null}
        </FullScreenMenu>
      </div>
    );

    let area;
    if (bounds && bounds.length === 4) {
      area =
        (Math.PI / 180) *
        Math.pow(6371, 2) *
        Math.abs(Math.sin(bounds[0]) - Math.sin(bounds[2])) *
        Math.abs(bounds[1] - bounds[3]);
    }
    const boundsString = bounds.join(",");
    return (
      <InstantSearch
        appId={config("ALGOLIA_APP_ID")}
        apiKey={config("ALGOLIA_SEARCH_API_KEY")}
        indexName={config("ALGOLIA_INDEX")}
      >
        {area && area > 40000 ? (
          <Configure insideBoundingBox={boundsString} />
        ) : null}
        {latlng && area < 40000 ? (
          <Configure aroundLatLng={latlng} aroundRadius={250000} />
        ) : null}
        <Configure hitsPerPage={16} />
        <Configure filters={refinementsQuery} />
        <div className={wrapper}>
          {desktopDisplay}
          {mobileDisplay}
          {searchComponent && searchComponent.displayAddress ? (
            <h3 className={locationDisplay}>
              Events near {searchComponent.displayAddress}
            </h3>
          ) : (
            ""
          )}
          <PillHandler />
          <Content />
        </div>
      </InstantSearch>
    );
  }
}

export default geolocated({
  positionOptions: {
    enableHighAccuracy: false
  },
  userDecisionTimeout: 5000
})(
  connect(
    (state, ownProps) => ({
      tags: state.search.tags,
      searchComponent: state.search.searchComponent,
      content: state.content
    }),
    {
      clearRefinements,
      getTags,
      setRefinements
    }
  )(CMSComponent(null, SearchComponent, 70))
);
